import { Store } from 'redux'
import * as Sentry from '@sentry/react'

import { IWindowJsEnv } from './interfaces'
import { IRootState } from './reducers/interface'
import { SentryConfig } from '../../constants/sentry_config'

const EnvCheck = /production|staging/

// Holds the Redux store for user information
let store: Store<IRootState> | undefined
const _window = window as unknown as IWindowJsEnv

export function setSentryStore (_store: Store<IRootState>) {
  store = _store
}

Sentry.init({
  dsn: SentryConfig.dsn,
  environment: _window.js_env.env,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration()
  ],
  // Tracing
  tracesSampleRate: 0.1, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/develop.staging.lopo.social/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

Sentry.addEventProcessor((event) => {
  if (!event.exception) return event

  const errorMessage = event.exception.values?.[0]?.value || ''
  const ignoredMessages = [
    'Failed to fetch',
    'NetworkError when attempting to fetch resource.',
    'Network request failed',
    'Fehler bei "fetch"',
    'Der Vorgang konnte nicht abgeschlossen werden. Die Software hat einen Verbindungsabbruch verursacht',
    'Es besteht anscheinend keine Verbindung zum Internet.',
    'Die Netzwerkverbindung wurde unterbrochen.',
    'Zeitüberschreitung bei der Anforderung.',
    'The operation is insecure.',
    'Abgebrochen',
    'cancelled',
    "Failed to read the 'cssRules' property from 'CSSStyleSheet': Cannot access rules",
    'Invalid LatLng object: (NaN, NaN)',
    'Abort due to cancellation of share.',
    'Share canceled',
    'Internal error: could not connect to Web Share interface.',
    'error on copying share link to clipboard',
    'The requested area is not yet released',
    'User denied Geolocation',
    'User denied geolocation prompt',
    'Could not load "util".',
    'The user aborted a request.'
  ]

  if (ignoredMessages.some((msg) => errorMessage.includes(msg))) {
    return null
  }

  return event
})

// Add user context to events
Sentry.addEventProcessor((event) => {
  try {
    const userData = store?.getState().user
    const extensionId = userData?.selectedProfileId
    const userEmail = userData?.email

    event.user = {
      ...event.user,
      id: extensionId,
      email: userEmail
    }
  } catch (error) {
    console.error('Error adding user context to Sentry event:', error)
  }
  return event
})

// Ignore errors originating from browser extensions
Sentry.addEventProcessor((event) => {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
  if (frames.some((frame) => frame.filename?.startsWith('moz-extension://'))) {
    return null
  }
  return event
})

// Only log errors from the allowed environment
Sentry.addEventProcessor((event) => {
  if (!EnvCheck.test(_window.js_env.env)) {
    return null
  }

  event.tags = {
    ...event.tags,
    environment: _window.js_env.env,
    version: _window.js_env.gitRevision
  }

  return event
})

// Add version info to the context
Sentry.addEventProcessor((event) => {
  event.tags = {
    ...event.tags,
    version: _window.js_env.gitRevision
  }
  return event
})

export const buildSentryErrortUrl = (id: string): string =>
  `https://beeftraeger.mvp-lab.de/organizations/lopo/issues/${id}/`
